import { TitleCasePipe } from '@angular/common';
import { Component, computed, inject, Input, OnInit, signal, viewChild, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Auth0Store } from '../../../modules/auth0/store';

import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { MatTooltip } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { NgxZendeskWebwidgetService } from 'ngx-zendesk-webwidget';
import { environment } from '../../../../environments/environment';
import { FeatureBit } from '../../../modules/auth0/models';
import { SignupAccessService } from '../../../modules/auth0/services/signup-access.service';
import { UserUtils } from '../../../modules/auth0/utils/user.utils';
import { CommunityForumService } from '../../services/community-forum.service';
import { AnalysisSearchBarComponent } from '../../components/analysis-search-bar/analysis-search-bar.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { ClickCursorDirective } from '../../directives/click-cursor.directive';
import { TextOverflowEllipsisDirective } from '../../directives/text-overflow-ellipsis.directive';
import { I18nPipe } from '../../i18n';
import { LanguageSelectorComponent } from '../../i18n/language-selector/language-selector.component';
import { RtlDirective } from '../../i18n/rtl.directive';
import { MiniApps } from '../../models';

@Component({
  host: {
    '[class.small]': 'small',
  },
  selector: 'gnx-top-nav',
  templateUrl: './top-nav.component.html',
  styleUrls: ['./top-nav.component.scss'],
  imports: [
    RouterLink,
    RtlDirective,
    SearchBarComponent,
    AnalysisSearchBarComponent,
    MatMenuTrigger,
    ClickCursorDirective,
    LanguageSelectorComponent,
    MatMenu,
    MatMenuItem,
    TextOverflowEllipsisDirective,
    MatTooltip,
    TitleCasePipe,
    I18nPipe,
  ],
})
export class TopNavComponent implements OnInit {
  private ngxZendeskService = inject(NgxZendeskWebwidgetService);
  private signupAccessService = inject(SignupAccessService);
  private router = inject(Router);
  private communityForum = inject(CommunityForumService);

  protected auth0Store = inject(Auth0Store);

  showCommunityForum = computed(() => !this.auth0Store.userDetails()?.org_config?.communityConfig?.hideCommunityForum);

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  @Input() small: boolean;
  readonly activeMiniApp = input<MiniApps>(undefined);
  readonly trigger = viewChild(MatMenuTrigger);

  protected readonly environment = environment;
  protected readonly Object = Object;

  FeatureBit = FeatureBit;
  languageSelectorEnabled = signal(false);
  isSignupAllowed: boolean;

  isAdmin = computed(() => {
    const userDetails = this.auth0Store.userDetails();
    return userDetails ? UserUtils.isUserAnAdmin(userDetails) : false;
  });

  showAssets = computed(() => {
    const fb = this.auth0Store.featureBits();
    return !!fb[FeatureBit.Assets] || fb[FeatureBit.Samples];
  });

  readonly MINI_APPS = [
    {
      app: MiniApps.ClinicalDB,
      label: 'SEARCH',
      icon: 'assets/svg/clinical-db.svg',
      path: '/clinical-db/home',
    },
    {
      app: MiniApps.MyVariants,
      label: 'KNOWLEDGE_BASE',
      icon: 'assets/svg/my-variants.svg',
      path: '/clinical-db/knowledge-base',
    },
    {
      app: MiniApps.AnalysisTool,
      label: 'MY_CASES',
      icon: 'assets/svg/analyze-tool.svg',
      path: '/analysis-tool',
    },
  ];

  constructor() {
    this.isSignupAllowed = this.signupAccessService.isSignupAllowed();
  }

  ngOnInit() {
    window.addEventListener('keydown', (event) => {
      if (event.ctrlKey && event.shiftKey && event.code === 'KeyL') {
        this.languageSelectorEnabled.update((v) => !v);
      }
    });
  }

  setFeatureBit(featureBit: string, value: boolean) {
    this.auth0Store.setFeatureBit(featureBit, value);
  }

  logout() {
    this.auth0Store.logout();
  }

  setRedirectAfterAuth() {
    this.auth0Store.setRedirectAfterAuth();
  }

  openZendeskPopup() {
    this.ngxZendeskService.zE('webWidget', 'open');
  }

  goToHelpPage() {
    window.open('http://help.genoox.com/en/', '_BLANK');
  }

  goToReleaseUpdatesPage() {
    window.open('https://help.genoox.com/en/collections/4590364-version-release-updates', '_BLANK');
  }

  goToCommunityForum() {
    this.communityForum.openForumFlow('HELP_MENU');
  }

  showVariantSearchBar = computed(() => {
    const url = this.currentUrl();
    return url.startsWith('/clinical-db/variant/') || url.startsWith('/clinical-db/gene/');
  });

  showAnalysisSearchBar = computed(() => this.currentUrl().startsWith('/analysis-tool/analysis/'));

  learnMoreClicked() {
    if (!this.learnMoreDiscovered) {
      localStorage.setItem('learnMoreDiscovered', 'true');
    }
  }

  get learnMoreDiscovered() {
    return localStorage.getItem('learnMoreDiscovered');
  }
}
